// src/sections/FaqSection.tsx
import { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const FAQ_CTA_GRADIENT = 'linear-gradient(135deg, #FF9950 0%, #FF9950 100%)';

const spaceshipDrift = keyframes`
  0% {
    transform: translate3d(0, 0, 0) rotate(-6deg);
  }

  25% {
    transform: translate3d(10px, -6px, 0) rotate(-2deg);
  }

  50% {
    transform: translate3d(14px, 6px, 0) rotate(3deg);
  }

  75% {
    transform: translate3d(4px, 12px, 0) rotate(1deg);
  }

  100% {
    transform: translate3d(0, 0, 0) rotate(-6deg);
  }
`;

/* ─────────────────────────────────────────
   1.  Layout wrappers
────────────────────────────────────────── */
const Wrapper = styled.section`
  scroll-snap-align: start;
  min-height: 100vh;
  padding: 128px 1.5rem 96px;
  background: #efecf0;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #000;
`;

const Inner = styled.div`
  max-width: 1440px;
  width: 100%;
  display: grid;
  gap: 5rem;

  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
    align-items: flex-start;
  }
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3.5rem;
`;

/* ─────────────────────────────────────────
   2.  Heading
────────────────────────────────────────── */
const Heading = styled.h2`
  font-size: clamp(3.25rem, 7.5vw, 4.75rem);
  line-height: 1.08;
  margin: 0 0 4rem;
  max-width: 14ch;

  em {
    font-family: serif;
    font-style: italic;
    font-weight: inherit;
  }
`;

/* ─────────────────────────────────────────
   3.  FAQ accordion
────────────────────────────────────────── */
const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Item = styled.div`
  border-bottom: 1px solid #ececec;
  padding: 1rem 0;
`;

const Question = styled.button<{ open: boolean }>`
  all: unset;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-size: 20px;
  font-weight: 500;
  line-height: 1.4;

  svg {
    transform: rotate(${p => (p.open ? '180deg' : '0deg')});
    transition: transform 0.25s ease;
  }
`;

const Answer = styled.p<{ open: boolean }>`
  max-height: ${p => (p.open ? '500px' : '0')};
  opacity: ${p => (p.open ? 1 : 0)};
  overflow: hidden;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 0.65rem 0 0;
  color: #555;
`;

/* simple chevron */
const Chevron = () => (
  <svg width="16" height="10" viewBox="0 0 16 10" fill="none">
    <path
      d="M1 1l7 7 7-7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ─────────────────────────────────────────
   4.  CTA card (right column)
────────────────────────────────────────── */
const CallCard = styled.div`
  position: relative;
  border-radius: 24px;
  background: ${FAQ_CTA_GRADIENT};
  color: #fff;
  width: 100%;
  margin-left: auto;
  max-width: 460px;
  padding: 2.75rem 2.25rem 3rem;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.22);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 50%, rgba(255, 153, 80, 0.18) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 153, 80, 0.18) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const Avatar = styled.img`
  position: relative;
  z-index: 1;
  display: block;
  width: min(100%, 198px);
  height: 128px;
  object-fit: contain;
  object-position: left center;
  margin-bottom: 1.9rem;
  transform-origin: center center;
  animation: ${spaceshipDrift} 8.5s ease-in-out infinite;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CallHeading = styled.h3`
  position: relative;
  z-index: 1;
  font-size: 2.25rem;
  line-height: 1.15;
  margin: 0 0 0.8rem;
`;

const CallSubheading = styled.p`
  position: relative;
  z-index: 1;
  margin: 0 0 1.5rem;
  font-size: 1.35rem;
  font-weight: 600;
  line-height: 1.2;
`;

const CallButton = styled.a`
  position: relative;
  z-index: 1;
  display: block;
  text-align: center;
  background: #fff;
  color: #000;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.85rem 1rem;
  font-size: 1rem;
  text-decoration: none;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const AltContact = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  margin-top: 2.25rem;
  font-size: 0.9rem;

  svg {
    flex-shrink: 0;
    margin-right: 0.65rem;
  }

  a {
    color: #fff;
    text-decoration: underline;
    margin-right: auto;
  }

  button {
    background: rgba(255, 255, 255, 0.25);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    cursor: pointer;
    font-size: 1.15rem;
    color: #fff;
  }
`;

/* ─────────────────────────────────────────
   5.  Data
────────────────────────────────────────── */
const FAQS = [
  {
    q: 'How quickly will I get my designs or marketing assets?',
    a: 'Most tasks are delivered within 1–2 business days depending on complexity and queue volume. Urgent requests are prioritized when possible.',
  },
  {
    q: 'How many requests can I submit?',
    a: 'You can submit unlimited requests. We work through them one at a time in a queue system to ensure quality and speed.',
  },
  {
    q: 'Can you handle social media or SEO?',
    a: 'Absolutely. We support social media design, content creation, local SEO optimization, and marketing collateral such as email templates and ad creatives.',
  },
  {
    q: 'What kind of businesses do you work with?',
    a: 'We primarily work with Tucson-based small businesses, eCommerce brands, and solo founders who want a reliable creative and marketing partner without the agency price tag.',
  },
  {
    q: 'Is there a contract or long-term commitment?',
    a: 'Nope. Our plans are monthly and you can pause or cancel at any time with no penalties.',
  },
  {
    q: 'Do I own all the work you create for me?',
    a: 'Yes! Every asset we create for your business is 100% yours to use however you’d like.',
  },
];

/* ─────────────────────────────────────────
   6.  Component
────────────────────────────────────────── */
export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <Wrapper id="faq">
      <Inner>
        {/* ───────────────  LEFT COLUMN  ─────────────── */}
        <LeftCol>
          <Heading>
            <em>Frequently</em> asked questions
          </Heading>

          <FaqList>
            {FAQS.map((item, i) => {
              const open = i === openIndex;
              return (
                <Item key={i}>
                  <Question
                    open={open}
                    onClick={() => setOpenIndex(open ? null : i)}
                  >
                    {item.q}
                    <Chevron />
                  </Question>
                  <Answer open={open}>{item.a}</Answer>
                </Item>
              );
            })}
          </FaqList>
        </LeftCol>

        {/* ───────────────  CTA CARD  ─────────────── */}
        <CallCard>
          <Avatar src="/images/cybardSpaceship2.png" alt="Cybard spaceship" />


          <CallHeading>Have a project in mind?</CallHeading>
          <CallSubheading>Contact us today.</CallSubheading>

          {/* Primary CTA – opens email client */}
          <CallButton href="tel:+15202303957">Call now</CallButton>

          {/* Secondary text link */}
          <AltContact>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 12l18-9-8 18-2-8-8-1z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>

            <a href="mailto:cybard.design@gmail.com">cybard.design@gmail.com</a>
            <button aria-label="email arrow">➜</button> 
          </AltContact>
        </CallCard>
      </Inner>
    </Wrapper>
  );
}
